// What an edit to a contact actually sends, and whether it needs confirming.
//
// Pure, and separate from the form, for this repository's standing reason: there is no
// DOM harness, so a decision left inside a component is a decision nothing can check.
//
// TWO THINGS HERE ARE EASY TO GET WRONG AND SILENT WHEN WRONG.
//
//   * Sending every field on every save. `PATCH /api/customers/:id` treats an absent
//     key as "leave it alone" and a present one as an instruction, so a form that
//     always posts its whole state turns "I fixed a phone number" into a write of the
//     name, the position and the account too — and the account write drags in the
//     confirmation rail for an edit nobody meant to make.
//   * Deciding locally whether the confirmation is needed. It is not this file's
//     decision: the server refuses the save and says so (see `accountChangeFrom`
//     below). What this predicts is only whether to EXPECT that refusal, so the form
//     can put the dialog in front of the agent rather than showing them a 422.

/** The fields this form may write. `email` is deliberately absent — see the route. */
const EDITABLE = ['name', 'phone', 'position', 'organization_id', 'location_id'];

/** `null`, `undefined` and `''` are the same thing in a form; `0` is not. */
const same = (a, b) => String(a ?? '') === String(b ?? '');

/**
 * Only what changed, as the PATCH body.
 *
 * An empty result means "nothing to save", which the caller should treat as a no-op
 * rather than a request — a PATCH that sends `{}` is a round trip and an audit-trail
 * risk for a form somebody opened and closed.
 *
 * `''` becomes `null` for the two id fields, because a cleared picker means "unlink
 * them" and the API distinguishes that from "leave it alone" by the key being present.
 * For the text fields an empty string is passed through as null for the same reason:
 * clearing a position is a real edit.
 *
 * @param {object} before  The customer as loaded.
 * @param {object} draft   The form's current state.
 * @returns {object} Possibly empty.
 */
export function changedFields(before, draft) {
  const out = {};
  for (const field of EDITABLE) {
    if (same(before?.[field], draft?.[field])) continue;
    const value = draft?.[field];
    out[field] = value === '' || value === undefined ? null : value;
  }
  return out;
}

/**
 * Whether this edit moves the contact between accounts.
 *
 * A prediction, not a rule. The rail is server-side (`acknowledge_account_change`),
 * because a confirmation an API client never sees is not a rail — this only decides
 * whether the form should raise the dialog itself instead of letting the agent meet a
 * 422 they cannot interpret.
 */
export function movesAccount(patch) {
  return Object.prototype.hasOwnProperty.call(patch ?? {}, 'organization_id')
    || Object.prototype.hasOwnProperty.call(patch ?? {}, 'location_id');
}

/**
 * The before/after the server sent with its refusal, or null.
 *
 * Read from the error rather than recomputed from the form, deliberately. The server
 * resolves ids to NAMES and works out the BRAND — which is not a field on this form at
 * all, because brand hangs off the organization — so a dialog built from local state
 * could not mention the one consequence the agent did not choose.
 */
export function accountChangeFrom(error) {
  const change = error?.details?.account_change;
  if (!change || typeof change !== 'object') return null;
  return {
    organization: change.organization ?? { from: null, to: null },
    location: change.location ?? { from: null, to: null },
    brand: change.brand ?? { from: null, to: null },
  };
}

/**
 * The rows a confirmation dialog shows: only what is actually moving.
 *
 * A dialog listing three rows where one changed makes the reader find the difference,
 * which is the job the dialog was supposed to do for them. Brand is included whenever
 * it moves even though nobody chose it — especially then.
 */
export function accountChangeRows(change) {
  if (!change) return [];
  return [
    ['Organization', change.organization],
    ['Store', change.location],
    ['Brand', change.brand],
  ]
    .filter(([, pair]) => String(pair?.from ?? '') !== String(pair?.to ?? ''))
    .map(([label, pair]) => ({
      label,
      from: pair.from ?? 'none',
      to: pair.to ?? 'none',
    }));
}
